import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { resolveInviteToken } from '../lib/invites';
import { addGroupMember } from '../lib/firestore/groups';
import { createRSVP, getEvent } from '../lib/firestore/events';
import { doc, getDoc, addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatEventDateTime } from '../lib/date-utils';
import '../styles/InviteResolver.css';

const InviteResolver = () => {
  const { token } = useParams();
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [event, setEvent] = useState(null);
  const [selectedRSVP, setSelectedRSVP] = useState(null); // 'going', 'maybe', 'no'
  const [showRSVPOptions, setShowRSVPOptions] = useState(false);

  useEffect(() => {
    console.log('🔵 [InviteResolver] useEffect triggered:', { token, hasUser: !!user });
    if (token) {
      console.log('🔵 [InviteResolver] Token found, storing and resolving...');
      // Store token in sessionStorage for signup page to pick up
      if (!user) {
        sessionStorage.setItem('pendingInviteToken', token);
        console.log('🔵 [InviteResolver] Stored pendingInviteToken (user not logged in)');
      }
      resolveInvite();
    } else {
      console.log('🔴 [InviteResolver] No token found');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resolveInvite = async () => {
    console.log('🔵 [InviteResolver] ====== resolveInvite STARTED ======');
    console.log('🔵 [InviteResolver] Token:', token);
    setLoading(true);
    setError('');

    try {
      console.log('🔵 [InviteResolver] Calling resolveInviteToken...');
      const resolved = await resolveInviteToken(token);
      console.log('🔵 [InviteResolver] Resolved invite:', resolved);

      if (!resolved) {
        console.log('🔴 [InviteResolver] Invalid or expired invite link');
        setError('Invalid or expired invite link');
        setLoading(false);
        return;
      }

      console.log('🔵 [InviteResolver] Invite type:', resolved.target.kind);
      console.log('🔵 [InviteResolver] Invite target:', resolved.target);

      // If it's an event invite, redirect directly to event page IMMEDIATELY
      // Do this BEFORE setting any state to prevent rendering
      if (resolved.target.kind === 'event_join') {
        console.log('🟢 [InviteResolver] ====== EVENT INVITE DETECTED ======');
        console.log('🟢 [InviteResolver] Event ID:', resolved.target.id);
        // Store invite token for RSVP processing
        sessionStorage.setItem('pendingInviteToken', token);
        console.log('🟢 [InviteResolver] Stored pendingInviteToken');
        const eventUrl = `/events/${resolved.target.id}?invite=${token}`;
        console.log('🟢 [InviteResolver] Navigating to:', eventUrl);
        // Use React Router navigate instead of window.location to avoid full page reload
        // This preserves React state and avoids auth redirects
        navigate(eventUrl, { replace: true });
        console.log('🟢 [InviteResolver] Navigate called - should redirect now');
        return; // Exit immediately - component will unmount
      }

      // For non-event invites, continue with normal flow
      console.log('🔵 [InviteResolver] Non-event invite, continuing with normal flow');
      setInviteData(resolved);

      // If user is logged in, try to process immediately
      if (user && !authLoading) {
        console.log('🔵 [InviteResolver] User logged in, processing invite...');
        await processInvite(resolved);
      } else {
        console.log('🔵 [InviteResolver] User not logged in');
        // If not logged in and it's a contact invite, redirect to signup
        if (resolved.target.kind === 'contact_invite') {
          console.log('🔵 [InviteResolver] Contact invite, redirecting to signup');
          navigate(`/signup?invite=${token}`);
        } else {
          // For other invites, show the invite card
          console.log('🔵 [InviteResolver] Showing invite card');
          setLoading(false);
        }
      }
    } catch (err) {
      console.error('🔴 [InviteResolver] Error resolving invite:', err);
      setError(err.message || 'Failed to process invite');
      setLoading(false);
    }
    console.log('🔵 [InviteResolver] ====== resolveInvite COMPLETED ======');
  };

  const processInvite = async (invite) => {
    try {
      if (invite.target.kind === 'group_join') {
        // Prepare member data
        const memberData = {
          userId: user.uid,
          role: invite.metadata?.role || 'member',
          joinedAt: new Date(),
        };
        
        // Only add sponsoredBy if it's a valid string
        const sponsorId = invite.metadata?.sponsorId || invite.issuedBy;
        if (sponsorId && typeof sponsorId === 'string') {
          memberData.sponsoredBy = sponsorId;
        }
        
        // Add user to group
        await addGroupMember(invite.target.id, memberData);

        navigate(`/groups/${invite.target.id}?joined=true`);
      } else if (invite.target.kind === 'event_join') {
        // Check if there's a stored RSVP choice from before login
        const storedRSVP = sessionStorage.getItem('pendingRSVP');
        let rsvpState = 'going'; // default
        
        if (storedRSVP) {
          // Map stored choice to RSVP state
          if (storedRSVP === 'going') {
            rsvpState = 'going';
          } else if (storedRSVP === 'maybe') {
            rsvpState = 'interested';
          } else if (storedRSVP === 'no') {
            rsvpState = 'cancelled';
          }
          // Clear stored RSVP
          sessionStorage.removeItem('pendingRSVP');
        } else {
          // Use default based on invite metadata
          rsvpState = invite.metadata?.role === 'guest' ? 'interested' : 'going';
        }
        
        // RSVP to event
        await createRSVP({
          userId: user.uid,
          eventId: invite.target.id,
          state: rsvpState,
          plusOnes: 0,
        });

        navigate(`/events/${invite.target.id}?joined=true`);
      } else if (invite.target.kind === 'profile_follow') {
        // Handle profile follow (future feature)
        navigate(`/home`);
      } else if (invite.target.kind === 'contact_invite') {
        // Add inviter to new user's contacts automatically
        await addInviterToContacts(invite);
        navigate(`/home?invited=true`);
      }
    } catch (err) {
      setError(err.message || 'Failed to process invite');
      setLoading(false);
    }
  };

  const addInviterToContacts = async (invite) => {
    if (!user) return;
    
    try {
      const inviterUserId = invite.target.id; // The person who invited
      const newUserId = user.uid; // The person who just signed up
      
      // Get inviter's user data
      const inviterDoc = await getDoc(doc(db, 'users', inviterUserId));
      if (!inviterDoc.exists()) {
        console.warn('Inviter user not found');
        return;
      }
      
      const inviterData = inviterDoc.data();
      
      // Get new user's phone number
      const newUserDoc = await getDoc(doc(db, 'users', newUserId));
      if (!newUserDoc.exists()) {
        console.warn('New user document not found');
        return;
      }
      
      const newUserData = newUserDoc.data();
      const newUserPhone = newUserData.phoneNumber || newUserData.phone;
      
      // Add inviter to new user's contacts
      const contactsRef = collection(db, 'users', newUserId, 'contacts');
      
      // Check if already exists
      const existingQuery = query(
        contactsRef,
        where('phoneE164', '==', inviterData.phone || inviterData.phoneNumber)
      );
      const existing = await getDocs(existingQuery);
      
      if (existing.empty) {
        // Create contact for inviter
        await addDoc(contactsRef, {
          name: `${inviterData.firstName || ''} ${inviterData.lastName || ''}`.trim() || 'Friend',
          phoneE164: inviterData.phone || inviterData.phoneNumber || null,
          email: inviterData.email || null,
          note: `Invited you to Hotelia`,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }
      
      // Also add new user to inviter's contacts (if they have phone number)
      if (newUserPhone) {
        const inviterContactsRef = collection(db, 'users', inviterUserId, 'contacts');
        const inviterExistingQuery = query(
          inviterContactsRef,
          where('phoneE164', '==', newUserPhone)
        );
        const inviterExisting = await getDocs(inviterExistingQuery);
        
        if (inviterExisting.empty) {
          await addDoc(inviterContactsRef, {
            name: `${newUserData.firstName || ''} ${newUserData.lastName || ''}`.trim() || 'Friend',
            phoneE164: newUserPhone,
            email: newUserData.email || null,
            note: `Joined via your invite`,
            createdAt: new Date(),
            updatedAt: new Date(),
          });
        }
      }
    } catch (error) {
      console.error('Error adding inviter to contacts:', error);
      // Don't throw - this is a nice-to-have feature
    }
  };

  const handleRSVPSelection = (rsvpChoice) => {
    // Store the RSVP choice
    setSelectedRSVP(rsvpChoice);
    sessionStorage.setItem('pendingRSVP', rsvpChoice);
    sessionStorage.setItem('pendingInviteToken', token);
    
    // Now prompt them to login/signup
    navigate('/signup', { 
      state: { 
        inviteToken: token,
        rsvpChoice: rsvpChoice 
      } 
    });
  };

  const handleAccept = async () => {
    if (!user) {
      // Store token in URL for signup to pick up
      navigate('/signup', { state: { inviteToken: token } });
      return;
    }

    if (inviteData) {
      setLoading(true);
      await processInvite(inviteData);
    }
  };

  // Always show loading while resolving - prevents flash of invite card for event invites
  if (loading || authLoading) {
    console.log('🔵 [InviteResolver] Rendering loading state:', { loading, authLoading });
    return (
      <div className="invite-resolver-container">
        <div className="loading">Processing invite...</div>
      </div>
    );
  }

  // If it's an event invite, we should have redirected already
  // But if we're still here somehow, show loading (redirect is in progress)
  if (inviteData?.target.kind === 'event_join') {
    return (
      <div className="invite-resolver-container">
        <div className="loading">Redirecting to event...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invite-resolver-container">
        <div className="error-message">{error}</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  if (!inviteData) {
    return (
      <div className="invite-resolver-container">
        <div className="error-message">Invalid invite</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="invite-resolver-container">
      <div className="invite-card">
        <h1>You're Invited!</h1>

        {inviteData.target.kind === 'group_join' && (
          <div className="invite-content">
            <p>You've been invited to join a group.</p>
            {!user ? (
              <div className="invite-actions">
                <button className="btn btn-primary" onClick={handleAccept}>
                  Sign In to Accept
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate('/signup')}
                >
                  Create Account
                </button>
              </div>
            ) : (
              <button className="btn btn-primary" onClick={handleAccept}>
                Join Group
              </button>
            )}
          </div>
        )}

        {/* Event invites are redirected immediately - this should never render */}

        {inviteData.target.kind === 'profile_follow' && (
          <div className="invite-content">
            <p>You've been invited to follow a profile.</p>
            {!user ? (
              <div className="invite-actions">
                <button className="btn btn-primary" onClick={handleAccept}>
                  Sign In
                </button>
              </div>
            ) : (
              <button className="btn btn-primary" onClick={handleAccept}>
                Accept
              </button>
            )}
          </div>
        )}

        {inviteData.target.kind === 'contact_invite' && (
          <div className="invite-content">
            <p>You've been invited to join Hotelia!</p>
            {inviteData.metadata?.inviterName && (
              <p className="invite-meta">
                Invited by: {inviteData.metadata.inviterName}
              </p>
            )}
            {!user ? (
              <div className="invite-actions">
                <button className="btn btn-primary" onClick={handleAccept}>
                  Create Account
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate('/login', { state: { inviteToken: token } })}
                >
                  Sign In
                </button>
                <p className="invite-hint">
                  After signing up, {inviteData.metadata?.inviterName || 'your friend'} will automatically be added to your contacts!
                </p>
              </div>
            ) : (
              <div>
                <p>You're already signed up! Your friend has been added to your contacts.</p>
                <button className="btn btn-primary" onClick={() => navigate('/home')}>
                  Go Home
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteResolver;


